import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { identifyOperator, resetOperator } from '@/lib/posthog_init';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('outplay_token'));

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('outplay_token');
      localStorage.removeItem('outplay_user');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Global listener — when api.js detects a 401 it dispatches this event so
  // we clear React state synchronously and ProtectedRoute kicks in immediately.
  useEffect(() => {
    const handler = () => {
      // Phase 261f — clear PostHog identity on forced expiry too.
      try { resetOperator(); } catch (_) { /* best-effort */ }
      setToken(null);
      setUser(null);
    };
    window.addEventListener('outplay:auth-expired', handler);
    return () => window.removeEventListener('outplay:auth-expired', handler);
  }, []);

  // Phase 261f — Reflect the current operator into PostHog so events flow
  // into per-user + per-academy dashboards. Fires whenever `user` changes —
  // including the initial /auth/me hydration AND the login mutation.
  // `identifyOperator` is idempotent + best-effort (never throws).
  useEffect(() => {
    if (user?.id) identifyOperator(user);
  }, [user?.id, user?.academy_id, user?.academy_name, user?.role]);

  // Phase 99 — Listen for an explicit user-refresh event (dispatched after
  // mutations like avatar change or permission grant). Re-pulls /auth/me so
  // the new permissions/avatar propagate instantly across the app.
  useEffect(() => {
    const refresh = () => { if (token) fetchUser(); };
    window.addEventListener('outplay-user-refresh', refresh);
    return () => window.removeEventListener('outplay-user-refresh', refresh);
  }, [token, fetchUser]);

  const login = async (email, password, role = null) => {
    // Phase 97 — Multi-role login. If role is provided, send it to disambiguate.
    const body = role ? { email, password, role } : { email, password };
    const res = await api.post('/auth/login', body);
    const { access_token, user: userData, first_login, password_must_reset } = res.data;
    localStorage.setItem('outplay_token', access_token);
    // Phase 326e — Stash the temp password so the forced-reset modal can
    // submit it as `current_password` without asking the user to re-type
    // their just-issued WhatsApp credentials.
    if (password_must_reset) {
      try { sessionStorage.setItem('outplay_temp_pw', password); } catch (_) { /* ignore */ }
      // Persist the flag on the user object so a reload still triggers it
      // (the modal sticks even if React state resets).
      userData.password_must_reset = true;
    }
    localStorage.setItem('outplay_user', JSON.stringify(userData));
    // Phase 282 — Mark a one-shot "first login" flag so the dashboard can
    // surface a welcome toast / onboarding modal exactly once. Cleared by
    // whichever screen surfaces it.
    if (first_login) {
      try { sessionStorage.setItem('outplay_first_login', '1'); } catch (_) { /* ignore */ }
    }
    setToken(access_token);
    setUser(userData);
    return { ...userData, first_login: !!first_login, password_must_reset: !!password_must_reset };
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    const { access_token, user: userData } = res.data;
    // Do NOT auto-login after registration — user must verify email first
    // Token is returned but not stored, so user stays "logged out"
    return userData;
  };

  const logout = () => {
    // Phase 261f — Reset PostHog identity FIRST so any final event in flight
    // attributes to the right operator (not the next person logging in on
    // this device).
    try { resetOperator(); } catch (_) { /* best-effort */ }
    localStorage.removeItem('outplay_token');
    localStorage.removeItem('outplay_user');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, token, setToken, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
